import { blade, bolt, groundRing, lotus, shield, slash, speed, star, sword, drop, crack } from '../kit';
import { C, mix, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * เพลงกระบี่ร่องศิลา (the starter art, yang): the sect's straight sword with a gold hilt and a
 * red tassel, and the nine qi-swords it calls.
 */

const TASSEL = '#e0402a';
const QI = '#ffe07a';

/** A small qi-sword (a flying blade with a gold hilt), from its hilt (x0,y0) to its point. */
function qiSword(p: Pix, x0: number, y0: number, x1: number, y1: number, col: string = C.steel): void {
  const len = Math.hypot(x1 - x0, y1 - y0);
  const dx = (x1 - x0) / len;
  const dy = (y1 - y0) / len;
  p.line(x0 + dx * 2, y0 + dy * 2, x1, y1, col);
  p.px(x1, y1, C.white);
  p.line(
    x0 + dx * 2 - dy * 1.5,
    y0 + dy * 2 + dx * 1.5,
    x0 + dx * 2 + dy * 1.5,
    y0 + dy * 2 - dx * 1.5,
    C.gold,
  );
  p.px(x0, y0, C.gold);
}

registerIcons('skill', {
  // Lightning called down through the raised sword.
  'ns-thunder': (p) => {
    bolt(
      p,
      [
        [30, 1],
        [21, 7],
        [27, 8],
        [19, 14],
      ],
      QI,
      4,
    );
    sword(p, 3, 29, 18, 14, { tassel: TASSEL });
    star(p, 19, 14, 3, C.white);
    p.px(24, 17, QI).px(14, 10, QI).px(27, 13, C.white);
  },
  // A thrust straight through a guard, which splits.
  'ns-pierce': (p) => {
    shield(p, 23, 3, 11, 14, '#9aa6b8', '#5a6478');
    crack(
      p,
      [
        [23, 4],
        [21, 8],
        [24, 11],
        [22, 16],
      ],
      C.ink,
    );
    speed(
      p,
      8,
      [
        [16, 5],
        [21, 6],
      ],
      QI,
    );
    sword(p, 2, 10, 30, 10, { tassel: TASSEL, guard: 4 });
    p.px(29, 8, C.white).px(30, 12, C.white).px(27, 6, QI);
  },
  // Nine qi-swords fanned out from the caster's glow.
  'ns-nine': (p) => {
    for (let k = 0; k < 9; k++) {
      const a = Math.PI + 0.25 + (k * (Math.PI - 0.5)) / 8;
      const r = k % 2 ? 13 : 11;
      qiSword(
        p,
        16 + Math.cos(a) * 4,
        25 + Math.sin(a) * 4,
        16 + Math.cos(a) * r * 1.05,
        25 + Math.sin(a) * r * 1.6,
        k % 2 ? C.steel : QI,
      );
    }
    p.ell(16, 26, 4, 3, QI);
    p.ell(16, 26, 2, 1.5, C.white);
  },
  // The sword upright over a lotus, a still ring of qi round it.
  'ns-calm': (p) => {
    p.ring(17, 13, 9, 9, mix(QI, C.white, 0.3), -1.2, 4.0);
    sword(p, 17, 24, 17, 3, { tassel: TASSEL, guard: 3 });
    lotus(p, 17, 28, 18, C.paper, '#ff9ab0');
  },
  // One great sweep round the caster, the sword at its leading edge.
  'ns-sweep': (p) => {
    slash(p, 16, 17, 13, 0.5, 5.4, QI, 3);
    sword(p, 9, 21, 27, 14, { tassel: TASSEL });
    p.px(4, 24, C.white).px(28, 24, QI);
  },
  // A dash forward on the sword's point, leaving afterimages.
  'ns-rush': (p) => {
    blade(p, 3, 22, 20, 5, { w: 2, col: mix(QI, C.ink, 0.45) });
    blade(p, 5, 25, 24, 6, { w: 2, col: mix(QI, C.ink, 0.15) });
    sword(p, 6, 28, 29, 5, { tassel: TASSEL });
    speed(
      p,
      13,
      [
        [25, 8],
        [29, 6],
      ],
      QI,
    );
    p.px(30, 3, C.white).px(28, 2, QI);
  },
  // The blood pact: a cut palm's drop runs down the raised sword, which wakes in red light.
  'ns-oath': (p) => {
    for (const [x, h] of [
      [11, 12],
      [23, 10],
      [13, 7],
      [21, 14],
    ] as const)
      p.line(x, 26, x, 26 - h, x % 2 ? '#ff6a4a' : C.blood);
    sword(p, 17, 29, 17, 3, { tassel: TASSEL, guard: 4 });
    drop(p, 24, 22, 2.6, C.blood);
    p.line(18, 8, 18, 18, C.blood);
  },
  // The heaven-slaying sword: one huge blade driven down from the sky into a ring of lightning.
  'ns-heaven': (p) => {
    groundRing(p, 16, 25, 11, 3.5, QI);
    for (const x of [6, 9, 23, 26]) p.line(x, 4 + (x % 3) * 2, x, 15 + (x % 2) * 4, x % 2 ? C.white : QI);
    crack(
      p,
      [
        [16, 26],
        [10, 28],
      ],
      QI,
    );
    crack(
      p,
      [
        [16, 26],
        [23, 28],
      ],
      QI,
    );
    blade(p, 16, 9, 16, 27, { w: 5, col: C.steel, edge: C.white, taper: 0.7 });
    p.thick(9, 8, 23, 8, C.gold, 2);
    p.rect(15, 3, 3, 5, C.woodDark);
    p.rect(14, 3, 5, 1, C.gold);
    p.line(17, 2, 21, 5, TASSEL);
  },
});
